import { ContentfulImage } from '@ecomm/contentful-components/ContentfulImage'
import { contentfulImageSchema } from '@ecomm/contentful-components/contentfulImageSchema'
import { getContentfulImageFromGatsbyData } from '@ecomm/contentful-image-utils/getContentfulImageFromGatsbyData'
import type { GatsbyImageSchema } from '@ecomm/contentful-schemas'
import equals from 'fast-deep-equal'
import type { GatsbyImageProps } from 'gatsby-plugin-image'
import React from 'react'
import { LegacyGatsbyImage } from './LegacyGatsbyImage'

type Props = Omit<GatsbyImageProps, 'alt' | 'image'> & {
  readonly image: GatsbyImageSchema
  readonly transformToContentfulImage?: boolean
  /** Overrides the default className prop for ContentfulImage if `transformToContentfulImage` is true */
  readonly classNameOverride?: string
}

function getContentfulImageData(image: GatsbyImageSchema) {
  if (!image || !image.gatsbyImageData) {
    return null
  }
  const transformedData = getContentfulImageFromGatsbyData(image)
  const parseResult = contentfulImageSchema.safeParse(transformedData)
  return parseResult.success ? parseResult.data : null
}

function getCombinedStyle(rest: Omit<GatsbyImageProps, 'alt' | 'image'>) {
  const { objectFit, objectPosition, imgStyle, style } = rest
  return {
    ...(objectFit && { objectFit }),
    ...(objectPosition && { objectPosition }),
    ...imgStyle,
    ...style
  }
}

function InternalGatsbyImage({ image, ...rest }: Props) {
  if (rest.transformToContentfulImage) {
    const contentfulImageData = getContentfulImageData(image)
    const { className, imgClassName, classNameOverride, loading, onClick } =
      rest
    const combinedStyle = getCombinedStyle(rest)
    return contentfulImageData ? (
      <ContentfulImage
        {...contentfulImageData}
        className={imgClassName || className}
        classNameOverride={classNameOverride}
        style={combinedStyle}
        loading={loading === 'eager' ? 'high' : 'lazy'}
        onClick={onClick}
      />
    ) : null
  } else {
    return <LegacyGatsbyImage image={image} {...rest} />
  }
}

/**
 * @deprecated Use `ContentfulImage` from `@ecomm/contentful-components` instead.
 * The performance for `GatsbyImage` is terrible in comparison to native HTML `img` elements with proper lazy loading.
 */
export const GatsbyImage = React.memo(InternalGatsbyImage, (prev, next) => {
  return equals(prev, next)
})
export type { GatsbyImageSchema }
